import { playSafely } from "./playSafely";

// VHS retries transport failures. This bounds an application's silent, error=null stall.
export function observePlaybackHealth(player, {
  refreshSource, sourceForUrl, mediaElement, isDvr = false,
  onState, onFailure, stallMs = 15000, maxAttempts = 2, requestMs = 10000
}) {
  let active = true, attempts = 0, pending = null, restore = null;
  let stopped = false, recovering = false, lastProgress = Date.now();
  let lastManifest = null, manifestChanged = Date.now(), waiting = false;
  let healthySince = null;
  let requestTimer = null;
  let operation = 0;
  const cancelRequest = request => {
    if (request && typeof request.cancel === "function") request.cancel();
  };
  const readProgress = () => {
    const media = mediaElement?.();
    const quality = media?.getVideoPlaybackQuality?.();
    if (media?.videoWidth > 0 && Number.isFinite(quality?.totalVideoFrames)) return quality.totalVideoFrames;
    if (media?.videoWidth > 0 && Number.isFinite(media?.webkitDecodedFrameCount)) return media.webkitDecodedFrameCount;
    if (media?.played?.length) {
      let total = 0;
      for (let i = 0; i < media.played.length; i++) total += media.played.end(i) - media.played.start(i);
      return total;
    }
    return player.readyState() >= 3 && !player.seeking() ? player.currentTime() : null;
  };
  let progress = readProgress();
  const removeRestore = () => {
    if (restore) player.off("loadeddata", restore);
    restore = null;
  };
  const fail = () => {
    stopped = true; recovering = false;
    cancelRequest(pending); pending = null;
    removeRestore();
    player.pause();
    onState("failed"); onFailure();
  };
  const recover = async () => {
    if (!active || recovering || player.isDisposed()) return;
    if (attempts >= maxAttempts || !refreshSource) { fail(); return; }
    recovering = true; healthySince = null; attempts += 1; onState("recovering");
    const position = isDvr ? player.currentTime() : null;
    const generation = ++operation;
    let timeout;
    try {
      const request = refreshSource();
      pending = request;
      const url = await Promise.race([
        request?.promise || request,
        new Promise((_, reject) => {
          timeout = setTimeout(() => { cancelRequest(request); reject(Error("Refresh timed out")); }, requestMs);
          requestTimer = timeout;
        })
      ]);
      clearTimeout(timeout);
      if (generation === operation) requestTimer = null;
      if (!active || stopped || generation !== operation || player.isDisposed()) return;
      pending = null;
      if (typeof url !== "string" || !url.trim()) throw Error("Missing playback source");
      if (isDvr && Number.isFinite(position)) {
        removeRestore();
        restore = () => {
          removeRestore();
          if (!active || player.isDisposed()) return;
          const ranges = player.seekable();
          const target = ranges?.length
            ? Math.max(ranges.start(0), Math.min(position, ranges.end(ranges.length - 1)))
            : position;
          player.currentTime(target);
        };
        player.on("loadeddata", restore);
      }
      player.src([sourceForUrl(url)]);
      progress = readProgress();
      lastProgress = manifestChanged = Date.now(); lastManifest = null;
      waiting = true;
      // play() may remain pending until frames arrive; it must not stop the watchdog.
      playSafely(player).then(played => {
        if (active && !played && !player.isDisposed() && player.paused()) onState("paused");
      });
    } catch (_) {
      if (!active || stopped || generation !== operation || player.isDisposed()) return;
      lastProgress = Date.now();
    } finally {
      clearTimeout(timeout);
      if (active && generation === operation) { recovering = false; pending = null; requestTimer = null; }
    }
  };
  const tick = () => {
    if (!active || stopped || recovering || player.isDisposed()) return;
    const now = Date.now();
    if (player.paused() || player.seeking()) {
      lastProgress = manifestChanged = now; progress = readProgress(); return;
    }
    const next = readProgress();
    if (next !== null && progress !== null && next > progress) {
      lastProgress = now; waiting = false; onState("playing");
      if (healthySince === null) healthySince = now;
      if (now - healthySince >= 60000) attempts = 0;
    }
    progress = next;
    let staleManifest = false;
    if (!isDvr) {
      const manifest = player.tech?.({ IWillNotUseThisInPlugins: true })?.vhs?.playlists?.media?.();
      if (manifest && !manifest.endList && Number.isFinite(manifest.mediaSequence)) {
        const signature = `${manifest.mediaSequence}:${manifest.segments?.length}:${manifest.segments?.slice(-1)[0]?.uri}`;
        if (signature !== lastManifest) { lastManifest = signature; manifestChanged = now; }
        staleManifest = now - manifestChanged >= Math.max(30000, (manifest.targetDuration || 10) * 3000);
      }
    }
    if (player.readyState() < 3 || waiting || staleManifest) { healthySince = null; onState("buffering"); }
    if (now - lastProgress >= stallMs || staleManifest) recover();
  };
  const onWaiting = () => { waiting = true; onState("buffering"); };
  const onPause = () => { lastProgress = manifestChanged = Date.now(); onState("paused"); };
  const onPlaying = () => { if (!player.paused()) onState("playing"); };
  const onMediaError = () => {
    stopped = true; operation++; recovering = false;
    cancelRequest(pending); pending = null; clearTimeout(requestTimer); requestTimer = null;
    removeRestore(); onState("failed");
  };
  const handlers = { waiting: onWaiting, stalled: onWaiting, pause: onPause, playing: onPlaying, error: onMediaError };
  Object.entries(handlers).forEach(([event, handler]) => player.on(event, handler));
  const timer = setInterval(tick, 1000);
  return {
    retry: () => { stopped = false; attempts = 0; recover(); },
    dispose: () => {
      active = false; operation++; clearInterval(timer); clearTimeout(requestTimer); cancelRequest(pending); pending = null; removeRestore();
      Object.entries(handlers).forEach(([event, handler]) => player.off(event, handler));
    }
  };
}
